<template>
  <MarketplaceCard
    v-if="config"
    v-model="toggle"
    logo-alt="Webhooks"
    description="Send events, such as a payment succeeding, to your own systems as signed HTTPS requests."
    :notice-text="noticeText"
    :disabled="config.read_only || saving || confirmingOff"
    cy-selector="marketplaceWebhooks"
  >
    <template #logo>
      <div class="mr-auto flex items-center gap-2">
        <LinkIcon class="h-8 w-8 text-gray-700" />
        <span class="text-xl font-semibold text-gray-900">Webhooks</span>
      </div>
    </template>
    <template #configure>
      <a
        v-if="config.enabled"
        href="/marketplace/webhooks"
        class="link"
        data-cy="webhooks-manage"
      >
        Manage webhooks
      </a>
      <span v-else class="text-gray">Manage webhooks</span>
    </template>
    <template #modal>
      <InlineConfirm
        v-if="confirmingOff"
        class="mt-2"
        confirm-label="Turn off webhooks"
        danger
        :busy="saving"
        data-cy="webhooks-turn-off-confirm"
        @confirm="turnOff"
        @cancel="cancelTurnOff"
      >
        {{ turnOffCopy }}
      </InlineConfirm>
    </template>
  </MarketplaceCard>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import LinkIcon from "@/images/link.svg?component";
import type { WebhooksConfig } from "@/vuex/modules/integrationWebhooks";
import MarketplaceCard from "./MarketplaceCard.vue";
import InlineConfirm from "./Webhooks/InlineConfirm.vue";
import { apiError, READ_ONLY_COPY } from "./Webhooks/webhooksHub";

const store = useStore();

const config = ref<WebhooksConfig | null>(null);
const saving = ref(false);
const confirmingOff = ref(false);

// What the switch shows. It goes off as soon as it's flipped, while the
// confirmation is up, and back on if the admin cancels.
const switchOn = ref(false);

const toggle = computed({
  get: () => switchOn.value,
  set: (value: boolean) => {
    switchOn.value = value;
    if (value) turnOn();
    else askTurnOff();
  }
});

const noticeText = computed(() => {
  if (!config.value) return "";
  if (config.value.read_only) return READ_ONLY_COPY;
  const { failing, disabled } = config.value.endpoints;
  const parts: string[] = [];
  if (failing) {
    parts.push(
      `${failing} ${failing === 1 ? "endpoint is" : "endpoints are"} failing`
    );
  }
  if (disabled) {
    parts.push(
      `${disabled} ${disabled === 1 ? "endpoint is" : "endpoints are"} disabled`
    );
  }
  return parts.length
    ? `${parts.join(" and ")}. Open Manage webhooks to see why.`
    : "";
});

const turnOffCopy = computed(() => {
  const count = config.value?.pending_deliveries_count ?? 0;
  const pending =
    count === 1
      ? "1 pending delivery is cancelled"
      : `${count.toLocaleString("en-US")} pending deliveries are cancelled`;
  return `Every webhook endpoint stops receiving events and ${pending}. Zapier is not affected.`;
});

function applyConfig(loaded: WebhooksConfig) {
  config.value = loaded;
  switchOn.value = loaded.enabled;
}

async function load() {
  try {
    applyConfig(await store.dispatch("fetchWebhooksConfig"));
  } catch {
    // The card stays hidden; the rest of the Marketplace still works.
    config.value = null;
  }
}

async function save(enabled: boolean) {
  saving.value = true;
  try {
    applyConfig(await store.dispatch("updateWebhooksConfig", enabled));
    window.notify.success(
      enabled ? "Webhooks turned on." : "Webhooks turned off."
    );
  } catch (error) {
    switchOn.value = !enabled;
    window.notify.error(
      apiError(error, "Webhooks couldn't be changed. Try again.").message
    );
  } finally {
    saving.value = false;
  }
}

function turnOn() {
  confirmingOff.value = false;
  save(true);
}

// The count in the question is fetched fresh, as it moves all the time.
async function askTurnOff() {
  confirmingOff.value = true;
  try {
    const fresh: WebhooksConfig = await store.dispatch("fetchWebhooksConfig");
    config.value = fresh;
  } catch {
    // Keep the count from the last load.
  }
}

async function turnOff() {
  await save(false);
  confirmingOff.value = false;
}

function cancelTurnOff() {
  confirmingOff.value = false;
  switchOn.value = true;
}

onMounted(load);
</script>
